import { MAX_PAGE_DEPTH } from './pageTreeUtils'
import type { PageDocument } from '@/lib/db/rxdb/hooks/entities/pages'
import { generateRankBetween } from '@/lib/utils/lexorank'

/**
 * Where a doc is moved to: a space or project (`projectId`), optionally nested
 * under another doc that lives there (`parentPageId`).
 */
export interface DocMoveTarget {
  projectId: string
  parentPageId: string | null
}

/** The location fields a move writes, for one page. */
export interface DocLocationPatch {
  id: string
  projectId: string
  parentPageId: string | null
  sortOrder: string | null
}

export type DocMoveBlockReason = 'missing' | 'noop' | 'cycle' | 'depth'

export type DocMovePlan =
  | {
      ok: true
      /** The moved doc first, then its descendants (parents before children). */
      updates: Array<DocLocationPatch>
      /** The same pages as they were, to undo the move. */
      previous: Array<DocLocationPatch>
    }
  | { ok: false; reason: DocMoveBlockReason }

type PageLike = Pick<PageDocument, 'id' | 'projectId' | 'parentPageId' | 'sortOrder' | 'createdOn' | 'isDeleted'>

function liveChildren<T extends PageLike>(pages: ReadonlyArray<T>, parentId: string): Array<T> {
  return pages.filter((p) => !p.isDeleted && p.parentPageId === parentId)
}

/** Every page nested under `pageId`, parents before their children. */
export function collectDescendantIds(pages: ReadonlyArray<PageLike>, pageId: string): Array<string> {
  const ids: Array<string> = []
  const seen = new Set<string>([pageId])
  const queue = [pageId]
  while (queue.length > 0) {
    const current = queue.shift()!
    for (const child of liveChildren(pages, current)) {
      if (seen.has(child.id)) continue
      seen.add(child.id)
      ids.push(child.id)
      queue.push(child.id)
    }
  }
  return ids
}

/** How many levels sit below `pageId` (0 for a leaf). */
export function subtreeHeight(pages: ReadonlyArray<PageLike>, pageId: string, seen = new Set<string>()): number {
  if (seen.has(pageId)) return 0
  seen.add(pageId)
  const children = liveChildren(pages, pageId)
  if (children.length === 0) return 0
  return 1 + Math.max(...children.map((c) => subtreeHeight(pages, c.id, seen)))
}

/** Depth of a page from its root (0 = top level). */
export function depthOf(pages: ReadonlyArray<PageLike>, pageId: string): number {
  const byId = new Map(pages.map((p) => [p.id, p]))
  let depth = 0
  const seen = new Set<string>([pageId])
  let parentId = byId.get(pageId)?.parentPageId ?? null
  while (parentId && !seen.has(parentId)) {
    seen.add(parentId)
    depth++
    parentId = byId.get(parentId)?.parentPageId ?? null
  }
  return depth
}

/**
 * Whether `docId` can be nested under `targetId` without creating a cycle or
 * pushing its deepest sub-page past MAX_PAGE_DEPTH.
 */
export function canNestUnder(pages: ReadonlyArray<PageLike>, docId: string, targetId: string): boolean {
  if (docId === targetId) return false
  if (collectDescendantIds(pages, docId).includes(targetId)) return false
  return depthOf(pages, targetId) + 1 + subtreeHeight(pages, docId) <= MAX_PAGE_DEPTH
}

function lastSiblingRank(
  pages: ReadonlyArray<PageLike>,
  projectId: string,
  parentPageId: string | null,
  excludeId: string,
): string | null {
  const siblings = pages
    .filter(
      (p) =>
        !p.isDeleted &&
        p.id !== excludeId &&
        p.projectId === projectId &&
        (p.parentPageId || null) === parentPageId,
    )
    .sort((a, b) => {
      if (a.sortOrder && b.sortOrder) return a.sortOrder.localeCompare(b.sortOrder)
      return a.createdOn - b.createdOn
    })
  return siblings.at(-1)?.sortOrder ?? null
}

const toPatch = (p: PageLike): DocLocationPatch => ({
  id: p.id,
  projectId: p.projectId,
  parentPageId: p.parentPageId || null,
  sortOrder: p.sortOrder,
})

/**
 * Plan moving a doc (and everything under it) to another space, project or
 * parent doc. The moved doc lands last among its new siblings; its sub-pages
 * keep their own nesting and order and just follow it to the new project.
 * Links and backlinks are keyed by page id, so nothing else changes.
 */
export function planDocMove(
  pages: ReadonlyArray<PageLike>,
  docId: string,
  target: DocMoveTarget,
): DocMovePlan {
  const doc = pages.find((p) => p.id === docId && !p.isDeleted)
  if (!doc) return { ok: false, reason: 'missing' }

  const parentPageId = target.parentPageId || null
  if (parentPageId) {
    const parent = pages.find((p) => p.id === parentPageId && !p.isDeleted)
    if (!parent) return { ok: false, reason: 'missing' }
    // A doc always lives in the same space or project as its parent.
    if (parent.projectId !== target.projectId) return { ok: false, reason: 'missing' }
    if (parentPageId === docId || collectDescendantIds(pages, docId).includes(parentPageId)) {
      return { ok: false, reason: 'cycle' }
    }
    if (!canNestUnder(pages, docId, parentPageId)) return { ok: false, reason: 'depth' }
  }

  if (doc.projectId === target.projectId && (doc.parentPageId || null) === parentPageId) {
    return { ok: false, reason: 'noop' }
  }

  // Sub-pages only need writing when the project changes; a re-nest inside
  // the same project leaves them exactly where they are.
  const descendants = doc.projectId === target.projectId
    ? []
    : collectDescendantIds(pages, docId)
        .map((id) => pages.find((p) => p.id === id))
        .filter((p): p is NonNullable<typeof p> => p !== undefined)

  const updates: Array<DocLocationPatch> = [
    {
      id: doc.id,
      projectId: target.projectId,
      parentPageId,
      sortOrder: generateRankBetween(lastSiblingRank(pages, target.projectId, parentPageId, doc.id), null),
    },
    ...descendants.map((p) => ({ ...toPatch(p), projectId: target.projectId })),
  ]

  return {
    ok: true,
    updates,
    previous: [toPatch(doc), ...descendants.map(toPatch)],
  }
}
